import { isContentClean, getFilterMessage } from '../utils/contentFilter';
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  updateDoc,
  onSnapshot,
  addDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { isFriend } from './socialService';

// ============================================================================
// TYPES
// ============================================================================

export interface Message {
  id: string;
  senderId: string;
  text: string;
  createdAt: Timestamp;
  read: boolean;
  // For special message types (mentions, etc.)
  type?: 'mention' | 'comment_mention' | 'system';
  storyId?: string;
}

export interface Conversation {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageAt: Timestamp;
  lastMessageBy: string;
  createdAt: Timestamp;
  lastReadAt?: { [userId: string]: Timestamp }; // Track when each user last read
  // Enriched fields (added client-side)
  otherUser?: {
    odwinId: string;
    username: string;
    photoURL?: string;
  };
  unreadCount?: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a consistent conversation ID from two user IDs
 * Always sorts IDs so the same two users always get the same conversation ID
 */
export const getConversationId = (uid1: string, uid2: string): string => {
  return [uid1, uid2].sort().join('_');
};

/**
 * Check if user can message another user (must be mutual friends)
 */
export const canMessage = async (currentUserId: string, targetUserId: string): Promise<boolean> => {
  if (currentUserId === targetUserId) return false;
  return await isFriend(currentUserId, targetUserId);
};

// ============================================================================
// CONVERSATION FUNCTIONS
// ============================================================================

/**
 * Get or create a conversation between two users
 */
export const getOrCreateConversation = async (
  currentUserId: string,
  targetUserId: string
): Promise<string> => {
  // First check if they're friends
  const areFriends = await canMessage(currentUserId, targetUserId);
  if (!areFriends) {
    throw new Error('You can only message friends (mutual follows)');
  }

  const conversationId = getConversationId(currentUserId, targetUserId);
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);

  if (!conversationSnap.exists()) {
    // Create new conversation
    await setDoc(conversationRef, {
      participants: [currentUserId, targetUserId].sort(),
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      lastMessageBy: '',
      createdAt: serverTimestamp(),
    });
  }

  return conversationId;
};

/**
 * Get a single conversation by ID
 */
export const getConversation = async (conversationId: string): Promise<Conversation | null> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (conversationSnap.exists()) {
      return {
        id: conversationSnap.id,
        ...conversationSnap.data(),
      } as Conversation;
    }
    return null;
  } catch (error) {
    console.error('Error getting conversation:', error);
    return null;
  }
};

/**
 * Get all conversations for a user
 */
export const getConversations = async (userId: string): Promise<Conversation[]> => {
  try {
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Conversation[];
  } catch (error) {
    console.error('Error getting conversations:', error);
    return [];
  }
};

/**
 * Subscribe to conversations for real-time updates
 */
export const subscribeToConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Conversation[];
    callback(conversations);
  }, (error) => {
    console.error('Error subscribing to conversations:', error);
  });
};

// ============================================================================
// MESSAGE FUNCTIONS
// ============================================================================

/**
 * Send a message in a conversation
 */
export const sendMessage = async (
  conversationId: string,
  senderId: string,
  text: string
): Promise<string> => {
  if (!text.trim()) {
    throw new Error('Message cannot be empty');
  }
  if (!isContentClean(text)) {
    throw new Error(getFilterMessage('chat'));
  }

  // Verify conversation exists and user is a participant
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  
  if (!conversationSnap.exists()) {
    throw new Error('Conversation not found');
  }

  const conversationData = conversationSnap.data();
  if (!conversationData.participants.includes(senderId)) {
    throw new Error('You are not a participant in this conversation');
  }

  // Add message to subcollection
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const messageDoc = await addDoc(messagesRef, {
    senderId,
    text: text.trim(),
    createdAt: serverTimestamp(),
    read: false,
  });

  // Update conversation with last message info
  await updateDoc(conversationRef, {
    lastMessage: text.trim().substring(0, 100), // Truncate for preview
    lastMessageAt: serverTimestamp(),
    lastMessageBy: senderId,
  });

  return messageDoc.id;
};

/**
 * Get messages for a conversation
 */
export const getMessages = async (
  conversationId: string,
  messageLimit: number = 50
): Promise<Message[]> => {
  try {
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(messageLimit)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      .reverse() as Message[]; // Reverse to get chronological order
  } catch (error) {
    console.error('Error getting messages:', error);
    return [];
  }
};

/**
 * Subscribe to messages for real-time updates
 */
export const subscribeToMessages = (
  conversationId: string,
  callback: (messages: Message[]) => void,
  messageLimit: number = 50
): (() => void) => {
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      .reverse() as Message[]; // Reverse to get chronological order
    callback(messages);
  }, (error) => {
    console.error('Error subscribing to messages:', error);
  });
};

/**
 * Mark conversation as read by updating lastReadAt timestamp
 */
export const markConversationAsRead = async (
  conversationId: string,
  userId: string
): Promise<void> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      [`lastReadAt.${userId}`]: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error marking conversation as read:', error);
  }
};

/**
 * Mark messages as read (legacy - now uses markConversationAsRead)
 */
export const markMessagesAsRead = async (
  conversationId: string,
  userId: string
): Promise<void> => {
  await markConversationAsRead(conversationId, userId);
};

/**
 * Get unread conversation count for a user
 * Compares lastMessageAt with user's lastReadAt
 */
export const getUnreadCount = async (userId: string): Promise<number> => {
  try {
    const conversations = await getConversations(userId);
    let unreadCount = 0;
    
    for (const convo of conversations) {
      // Skip if no messages yet
      if (!convo.lastMessageAt || !convo.lastMessageBy) continue;
      
      // Skip if user sent the last message (they've seen it)
      if (convo.lastMessageBy === userId) continue;
      
      // Check if user has read this conversation
      const lastReadAt = (convo as any).lastReadAt?.[userId];
      
      if (!lastReadAt) {
        // Never read - it's unread
        unreadCount++;
      } else {
        // Compare timestamps
        const lastMessageTime = convo.lastMessageAt.toMillis ? convo.lastMessageAt.toMillis() : 0;
        const lastReadTime = lastReadAt.toMillis ? lastReadAt.toMillis() : 0;
        
        if (lastMessageTime > lastReadTime) {
          unreadCount++;
        }
      }
    }
    
    return unreadCount;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Subscribe to unread count for real-time badge updates
 */
export const subscribeToUnreadCount = (
  userId: string,
  callback: (count: number) => void
): (() => void) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId)
  );

  return onSnapshot(q, (snapshot) => {
    let unreadCount = 0;
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Skip if no messages yet
      if (!data.lastMessageAt || !data.lastMessageBy) return;
      
      // Skip if user sent the last message
      if (data.lastMessageBy === userId) return;
      
      // Check lastReadAt
      const lastReadAt = data.lastReadAt?.[userId];
      
      if (!lastReadAt) {
        unreadCount++;
      } else {
        const lastMessageTime = data.lastMessageAt.toMillis ? data.lastMessageAt.toMillis() : 0;
        const lastReadTime = lastReadAt.toMillis ? lastReadAt.toMillis() : 0;
        
        if (lastMessageTime > lastReadTime) {
          unreadCount++;
        }
      }
    });
    
    callback(unreadCount);
  }, (error) => {
    console.error('Error subscribing to unread count:', error);
    callback(0);
  });
};

/**
 * Delete a conversation (both users must agree, or admin)
 * For now, just hide it from the user's view
 */
export const deleteConversation = async (
  conversationId: string,
  userId: string
): Promise<void> => {
  // In a real app, you might want to soft-delete or archive
  // For now, we'll actually delete if both users have left
  // This is a simplified version
  console.log('Delete conversation not fully implemented:', conversationId, userId);
};